import {
  useForm,
  SubmitHandler,
  FieldValues,
} from 'react-hook-form';
import { Modal, Button, Form } from 'react-bootstrap';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentPasswordForm } from '../types/user.types';

interface EditPasswordModalProps {
  show: boolean;
  saveUserInfo: (_result: boolean, _data: any) => void;
  passwordInvalid: boolean;
  passwordErrorMessage: string;
  setPasswordInvalid: (_result: boolean) => void;
}

function EditPasswordModal({
  show,
  saveUserInfo,
  passwordInvalid,
  passwordErrorMessage,
  setPasswordInvalid,
}: EditPasswordModalProps): JSX.Element {
  const { t } = useTranslation();
  const submitRef = useRef<HTMLButtonElement>(null);
  const [showWarning, setShowWarning] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<CurrentPasswordForm>();

  const onSubmit: SubmitHandler<FieldValues> = (data: FieldValues) => {
    if (data.newPassword === data.updatedPassword) {
      saveUserInfo(true, {
        newPassword: data.newPassword,
        currentPassword: data.currentPassword,
        type: 'password',
      });
      setShowWarning(false);
      setPasswordInvalid(false);
      reset();
    } else {
      setPasswordInvalid(false);
      setShowWarning(true);
    }
  };

  function handleClose(): void {
    setShowWarning(false);
    saveUserInfo(false, {});
    setPasswordInvalid(false);
    reset();
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      className="modal"
      backdrop="static"
      size="lg"
    >
      <Modal.Header className="text-center" closeButton>
        <Modal.Title>{t('Update_Password')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showWarning && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {t('Password_Update_Warning')}
          </div>
        )}
        {passwordInvalid && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {passwordErrorMessage}
          </div>
        )}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('Current_Password')}</Form.Label>
            <Form.Control
              type="password"
              {...register('currentPassword', { required: true })}
              isInvalid={!!errors.currentPassword}
            />
            {errors.currentPassword && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('NewPassword')}</Form.Label>
            <Form.Control
              type="password"
              {...register('newPassword', { required: true })}
              isInvalid={!!errors.newPassword}
            />
            {errors.newPassword && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('ConfirmNewPassword')}</Form.Label>
            <Form.Control
              type="password"
              {...register('updatedPassword', { required: true })}
              isInvalid={!!errors.updatedPassword}
            />
            {errors.updatedPassword && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <button
            ref={submitRef}
            type="submit"
            style={{ display: 'none' }}
            aria-label="submit"
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleClose}>
          {t('PDP.Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="col-3"
          onClick={() => { submitRef.current?.click(); }}
        >
          {t('PDP.Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditPasswordModal;
